import { useCallback, useRef, useEffect } from "react";

type AsyncFunction<T extends unknown[], R> = (...args: T) => Promise<R>;

export default function useDebounceAsync<T extends unknown[], R>(
  fn: AsyncFunction<T, R>,
  wait: number = 300
): AsyncFunction<T, R> {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: T) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      return new Promise<R>((resolve, reject) => {
        timeoutRef.current = setTimeout(() => {
          fn(...args)
            .then(resolve)
            .catch(reject);
        }, wait);
      });
    },
    [fn, wait]
  );
}
